import * as Sentry from "@sentry/react";

/**
 * Sentry initialization for the SafePath agent web portal.
 *
 * The DSN and environment come from the runtime config (/ClientConfig API,
 * surfaced via config.service.js) — the same pattern as logRocketAppId — so a
 * single build artifact serves every environment and values are managed in the
 * API's appsettings (dev/prod), not baked in at build time.
 *
 * `initSentry(config)` is called from index.js right after initConfig() resolves.
 * It no-ops when no DSN is configured (e.g. local dev).
 *
 * Config matches the mobile app's hardening:
 *  - environment from config.sentryEnv (dev/prod split, like SigNoz)
 *  - PII scrubbing + no auto-PII (sensitive immigration/legal data)
 *  - console breadcrumbs dropped (freeform message text can carry PII)
 *  - on-error-only, fully-masked session replay
 */

// Recursively redact sensitive keys before anything leaves the browser.
const SENSITIVE_KEY =
  /^(authorization|voicedata|password|token|refreshtoken|access_token|ssn)$/i;
function scrubSensitive(value, depth = 0) {
  if (depth > 6 || value == null || typeof value !== "object") return value;
  for (const key of Object.keys(value)) {
    if (SENSITIVE_KEY.test(key)) value[key] = "[REDACTED]";
    else value[key] = scrubSensitive(value[key], depth + 1);
  }
  return value;
}

let initialized = false;

/**
 * Initialize Sentry from the resolved runtime config. Safe to call once.
 * @param {{ sentryDsn?: string, sentryEnv?: string }} config
 */
export function initSentry(config) {
  if (initialized) return;
  const dsn = config?.sentryDsn;
  if (!dsn) return; // not configured for this environment — stay disabled

  const environment = config?.sentryEnv || "development";

  Sentry.init({
    dsn,
    environment,
    // Optional build-time release stamp so uploaded source maps resolve.
    release: process.env.REACT_APP_SENTRY_RELEASE,

    // Sensitive app: never auto-attach IP / cookies / etc.
    sendDefaultPii: false,
    enableLogs: true,

    integrations: [
      Sentry.browserTracingIntegration(),
      // Mask all text and block media so replays don't capture PII.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],

    // Light performance sampling in prod, full in dev.
    tracesSampleRate: environment === "production" ? 0.2 : 1.0,

    // Don't record every session (privacy + replay quota); always keep the
    // replay leading up to an error.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event) {
      if (event.request) scrubSensitive(event.request);
      if (event.contexts) scrubSensitive(event.contexts);
      if (event.extra) scrubSensitive(event.extra);
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      // Drop console breadcrumbs — their freeform text can carry PII that
      // key-based scrubbing can't catch. Keep navigation / fetch / UI breadcrumbs.
      if (breadcrumb.category === "console") return null;
      if (breadcrumb.data) scrubSensitive(breadcrumb.data);
      return breadcrumb;
    },
  });

  initialized = true;
}
